import { ActionSheet } from '@/components/ui/action-sheet';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '@/components/ui/dialog';
import { SafariInstallationInstructionsProps } from './types';
import { Share, SquarePlus, X } from 'lucide-react';

const iosInstructionSteps = [
  {
    content: (
      <>
        В меню Safari нажмите{' '}
        <Share
          absoluteStrokeWidth
          aria-hidden="true"
          size={20}
          strokeWidth={1.25}
        />{' '}
        «Поделиться»
      </>
    ),
    id: 1
  },
  {
    content: (
      <>
        Выберите{' '}
        <SquarePlus
          absoluteStrokeWidth
          aria-hidden="true"
          size={16}
          strokeWidth={1.25}
        />{' '}
        «На экран “Домой”»
      </>
    ),
    id: 2
  },
  {
    content: <>Если доступно, включите «Открыть как веб‑приложение»</>,
    id: 3
  },
  {
    content: <>Нажмите «Добавить»</>,
    id: 4
  }
];

const macosInstructionSteps = [
  {
    content: <>Откройте меню «Файл»</>,
    id: 1
  },
  {
    content: <>Выберите «Добавить в Dock»</>,
    id: 2
  },
  {
    content: <>Нажмите «Добавить»</>,
    id: 3
  }
];

const SafariInstallationInstructions = (props: SafariInstallationInstructionsProps) => {
  const {
    isOpen,
    onClose,
    onComplete,
    scenario
  } = props;
  const isIosScenario = scenario === 'SAFARI_IOS';
  const instructionSteps = isIosScenario ? iosInstructionSteps : macosInstructionSteps;
  const steps = (
    <ol className="flex flex-col gap-6">
      {instructionSteps.map((step) => (
        <li
          className="flex items-center gap-3 font-sans text-sm leading-5 text-foreground"
          key={step.id}
        >
          <span className="flex size-7 shrink-0 items-center justify-center text-sm font-semibold text-accent rounded-full bg-background">
            {step.id}
          </span>
          <span className="flex items-center gap-1">{step.content}</span>
        </li>
      ))}
    </ol>
  );

  if (isIosScenario) {
    return (
      <ActionSheet
        footer={null}
        onOpenChange={(nextIsOpen) => {
          if (!nextIsOpen) onClose();
        }}
        open={isOpen}
        title="Установка на экран «Домой»"
        titleId="safari-ios-installation-title"
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <p className="font-sans text-xs leading-4 text-muted-foreground">Safari на iPhone</p>
            {steps}
          </div>
          <Button
            className="w-full"
            onClick={onComplete}
            size="lg"
            type="button"
          >
            Понятно
          </Button>
        </div>
      </ActionSheet>
    );
  }

  return (
    <Dialog
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen) onClose();
      }}
      open={isOpen}
    >
      <DialogContent
        className="flex w-[calc(100%-32px)] max-w-[390px] flex-col gap-6 p-4 rounded-2xl border-border-strong bg-surface shadow-2xl sm:rounded-2xl"
        hideClose
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="font-sans text-lg font-semibold leading-6 text-foreground">
              Установка приложения
            </DialogTitle>
            <DialogClose asChild>
              <button
                className="inline-flex size-8 shrink-0 items-center justify-center text-muted-foreground rounded-full hover:bg-muted hover:text-foreground"
                aria-label="Закрыть"
                type="button"
              >
                <X
                  className="size-5"
                  aria-hidden="true"
                  strokeWidth={2}
                />
              </button>
            </DialogClose>
          </div>
          <DialogDescription className="font-sans text-sm leading-5 text-muted-foreground">
            Safari на macOS
          </DialogDescription>
        </div>
        {steps}
        <Button
          className="w-full"
          onClick={onComplete}
          size="md"
          type="button"
        >
          Понятно
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default SafariInstallationInstructions;